/**
 * SteckbriefModal – Spielerprofil als Fahndungsplakat (NT-STECKBRIEF-01).
 * Eigenständige, body-attachte Komponente (Muster wie PfandModal.ts) in der gemeinsamen
 * `.modal-backdrop`/`.modal`-Hülle (dashboard.css) — von jeder Seite aus aufrufbar über
 * `openSteckbrief(username)`. Zeigt Ruf, nicht Besitz und nicht Standorte.
 */

import { api, ApiError } from '../services/api.js';
import { showToast } from '../utils/toast.js';
import { renderWantedLevelHtml } from '../utils/wanted-labels.js';
import {
  UNBESCHRIEBENES_BLATT,
  bountyShares,
  focusLabel,
  formatBounty,
  renderSparkline,
} from '../utils/bounty-labels.js';

const MODAL_ID = 'steckbrief-modal';

interface SteckbriefAward {
  key: string;
  label: string;
  kind: 'TITLE' | 'BADGE';
  expired: boolean;
  colorVar: string;
}

interface SteckbriefResponse {
  username: string;
  isOwnProfile: boolean;
  profileLevel: number;
  streetRank: string;
  wantedLevel: number;
  activity: 'HEUTE_AKTIV' | 'DIESE_WOCHE' | 'UNTERGETAUCHT';
  memberSince: string;
  equippedTitle: SteckbriefAward | null;
  awards: SteckbriefAward[];
  achievements: {
    unlocked: number;
    totalVisible: number;
    secretUnlocked: number;
    secretTotal: number;
    recent: { name: string }[];
  } | null;
  org: { name: string; rank: string } | null;
  seekingOrg: boolean;
  viewerCanInvite: boolean;
  /** NT-KOPFGELD-01: nur Punkte. */
  bounty: {
    current: number;
    focus: string | null;
    history: number[];
    components: { umsatz: number; produktion: number; revier: number; logistik: number };
  } | null;
}

const ACTIVITY_LABELS: Record<SteckbriefResponse['activity'], string> = {
  HEUTE_AKTIV: 'HEUTE AKTIV',
  DIESE_WOCHE: 'DIESE WOCHE',
  UNTERGETAUCHT: 'UNTERGETAUCHT',
};

let current: SteckbriefResponse | null = null;
let busy = false;

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** "2026-05" → "05/2026" */
function formatMemberSince(value: string): string {
  const [year, month] = value.split('-');
  return year !== undefined && month !== undefined ? `${month}/${year}` : value;
}

export function removeSteckbrief(): void {
  document.getElementById(MODAL_ID)?.remove();
  current = null;
  busy = false;
}

/** Öffnet den Steckbrief eines Spielers (auch des eigenen). */
export async function openSteckbrief(username: string): Promise<void> {
  removeSteckbrief();

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.className = 'modal-backdrop';
  document.body.appendChild(overlay);
  attachListeners(overlay);

  renderMessage('STECKBRIEF WIRD ABGERUFEN...');
  await load(username);
}

async function load(username: string): Promise<void> {
  try {
    current = await api.get<SteckbriefResponse>(`/players/${encodeURIComponent(username)}/steckbrief`);
    render();
  } catch (err) {
    current = null;
    renderMessage(err instanceof ApiError && err.status === 404 ? 'KEIN EINTRAG UNTER DIESEM NAMEN' : 'AKTE NICHT AUFFINDBAR');
  }
}

function renderMessage(text: string): void {
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay) return;
  overlay.innerHTML = `
    <div class="modal modal--narrow stb-modal" role="dialog" aria-modal="true" aria-label="Steckbrief">
      <div class="stb-frame-title">STECKBRIEF</div>
      <div class="modal-body"><p class="stb-message">${esc(text)}</p></div>
      <div class="modal-actions"><button class="btn" data-close-modal>SCHLIESSEN</button></div>
    </div>
  `;
}

function renderAward(award: SteckbriefAward): string {
  const colorVar = award.expired ? '--text-muted' : award.colorVar;
  const title = award.expired ? ' title="Erloschen"' : '';
  return `<span class="badge stb-award${award.expired ? ' stb-award--expired' : ''}" style="color:var(${colorVar});border-color:var(${colorVar});"${title}>${esc(award.label)}</span>`;
}

/** KOPFGELD-Modul (NT-KOPFGELD-01) — sitzt am KOPFGELD-SLOT zwischen FAHNDUNG und STATUS. */
function renderBountySection(d: SteckbriefResponse): string {
  const b = d.bounty;
  if (b === null || b.current <= 0) {
    return `<div class="stb-row"><span class="stb-label">KOPFGELD</span><span class="stb-value stb-bounty-focus">${UNBESCHRIEBENES_BLATT}</span></div>`;
  }
  const focus = b.focus !== null ? ` · <span class="stb-bounty-focus">${esc(focusLabel(b.focus))}</span>` : '';
  const shares = bountyShares(b.components);
  return `
    <div class="stb-row"><span class="stb-label">KOPFGELD</span><span class="stb-value"><span class="stb-bounty-value">${formatBounty(b.current)}</span>${focus}</span></div>
    <div class="stb-row"><span class="stb-label">30 TAGE</span><span class="stb-value stb-spark" aria-label="Kopfgeld-Verlauf der letzten 30 Tage">${renderSparkline(b.history)}</span></div>
    <div class="stb-row"><span class="stb-label">ANTEILE</span><span class="stb-value stb-shares">UMSATZ ${String(shares.umsatz)}% · PRODUKTION ${String(shares.produktion)}% · REVIER ${String(shares.revier)}% · LOGISTIK ${String(shares.logistik)}%</span></div>
  `;
}

function renderOrgSection(d: SteckbriefResponse): string {
  const orgLine = d.org !== null
    ? `${esc(d.org.name)} · ${esc(d.org.rank.toUpperCase())}`
    : `KEINE ORG${d.seekingOrg ? ' · <span class="stb-seeking">SUCHT ORG</span>' : ''}`;

  // Schalter nur auf dem eigenen Profil und nur ohne Org — "sucht Org" ergibt in einer Org keinen Sinn.
  const toggle = d.isOwnProfile && d.org === null
    ? `<button type="button" class="stb-toggle" id="stb-seeking-toggle" ${busy ? 'disabled' : ''}>[ SUCHT ORG: ${d.seekingOrg ? 'AN' : 'AUS'} ]</button>`
    : '';

  return `
    <div class="stb-divider"></div>
    <div class="stb-row"><span class="stb-label">ORG</span><span class="stb-value">${orgLine}</span></div>
    ${toggle}
  `;
}

function render(): void {
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay || !current) return;
  const d = current;

  const titleLine = d.equippedTitle !== null
    ? `<div class="stb-title">»${esc(d.equippedTitle.label.toUpperCase())}«</div>`
    : '';

  // Abschnitte ohne Datenbasis werden nicht gerendert (S6) — keine leeren Kästen.
  const awardsSection = d.awards.length > 0
    ? `
      <div class="stb-divider"></div>
      <div class="stb-section-label">AUSZEICHNUNGEN</div>
      <div class="stb-awards">${d.awards.map(renderAward).join('')}</div>
    `
    : '';

  const achievementsSection = d.achievements !== null
    ? `
      <div class="stb-divider"></div>
      <div class="stb-row"><span class="stb-label">ACHIEVEMENTS</span><span class="stb-value">${String(d.achievements.unlocked)}/${String(d.achievements.totalVisible)} · GEHEIM ${String(d.achievements.secretUnlocked)}/${String(d.achievements.secretTotal)}</span></div>
      ${d.achievements.recent.length > 0
        ? `<div class="stb-row"><span class="stb-label">ZULETZT</span><span class="stb-value">${d.achievements.recent.map((a) => esc(a.name)).join(' · ')}</span></div>`
        : ''}
    `
    : '';

  const inviteButton = d.viewerCanInvite
    ? `<button class="btn btn--primary" id="stb-invite" ${busy ? 'disabled' : ''}>ZUR ORG EINLADEN</button>`
    : '';

  overlay.innerHTML = `
    <div class="modal modal--narrow stb-modal" role="dialog" aria-modal="true" aria-label="Steckbrief ${esc(d.username)}">
      <div class="stb-frame-title">STECKBRIEF</div>
      <div class="modal-body">
        <div class="stb-wanted">GESUCHT:&nbsp;<span class="stb-name">${esc(d.username.toUpperCase())}</span></div>
        ${titleLine}
        <div class="stb-row"><span class="stb-label">STRASSENRANG</span><span class="stb-value">${esc(d.streetRank.toUpperCase())}</span></div>
        <div class="stb-row"><span class="stb-label">LEVEL</span><span class="stb-value">${String(d.profileLevel)}</span></div>
        <div class="stb-row"><span class="stb-label">FAHNDUNG</span><span class="stb-value">${renderWantedLevelHtml(d.wantedLevel)}</span></div>
        <!-- KOPFGELD-SLOT (NT-KOPFGELD-01) -->
        ${renderBountySection(d)}
        <div class="stb-row"><span class="stb-label">STATUS</span><span class="stb-value stb-activity--${d.activity.toLowerCase()}">${ACTIVITY_LABELS[d.activity]}</span></div>
        <div class="stb-row"><span class="stb-label">BEKANNT SEIT</span><span class="stb-value">${esc(formatMemberSince(d.memberSince))}</span></div>
        ${renderOrgSection(d)}
        ${awardsSection}
        ${achievementsSection}
      </div>
      <div class="modal-actions">
        ${inviteButton}
        <button class="btn" data-close-modal>SCHLIESSEN</button>
      </div>
    </div>
  `;
}

function attachListeners(overlay: HTMLElement): void {
  overlay.addEventListener('click', (e) => {
    const target = e.target as Element;

    if (target === overlay || target.closest('[data-close-modal]')) {
      removeSteckbrief();
      return;
    }
    if (target.closest('#stb-seeking-toggle')) {
      void handleToggleSeeking();
      return;
    }
    if (target.closest('#stb-invite')) {
      void handleInvite();
    }
  });
}

async function handleToggleSeeking(): Promise<void> {
  // Referenz festhalten: wird das Modal während des Requests geschlossen, ist `current` danach null.
  const shown = current;
  if (!shown || busy) return;
  busy = true;
  render();
  try {
    const result = await api.patch<{ seekingOrg: boolean }>('/players/me/seeking-org', { seekingOrg: !shown.seekingOrg });
    shown.seekingOrg = result.seekingOrg;
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Änderung fehlgeschlagen.', 'error');
  } finally {
    busy = false;
    render();
  }
}

async function handleInvite(): Promise<void> {
  const shown = current;
  if (!shown || busy) return;
  busy = true;
  render();
  try {
    await api.post('/orgs/invites', { username: shown.username });
    showToast(`${shown.username} wurde eingeladen.`);
    shown.viewerCanInvite = false;
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Einladung fehlgeschlagen.', 'error');
  } finally {
    busy = false;
    render();
  }
}
